import React from 'react';
import { useCart } from '../context/CartContext';
import { Link, useNavigate } from 'react-router-dom';
import { FiShoppingCart, FiTrash2, FiArrowLeft, FiPlus, FiMinus, FiLock } from 'react-icons/fi';

const CartPage = () => {
  const {
    cart,
    cartTotal,
    cartItemCount,
    removeFromCart,
    updateQuantity,
    clearCart
  } = useCart();
  
  const navigate = useNavigate();

  // FIXED: Simple remove handler
  const handleRemoveItem = (item) => {
    console.log('Removing item:', item);
    removeFromCart(item.product_id, item.variation_id || null);
  };

  // FIXED: Increment quantity
  const handleIncrement = (item) => {
    const newQuantity = (item.quantity || 1) + 1;
    updateQuantity(item.product_id, item.variation_id || null, newQuantity);
  };

  // FIXED: Decrement quantity
  const handleDecrement = (item) => {
    const newQuantity = (item.quantity || 1) - 1;
    if (newQuantity >= 1) {
      updateQuantity(item.product_id, item.variation_id || null, newQuantity);
    }
  };

  const proceedToCheckout = () => {
    navigate('/checkout');
  };

  // Debug: log cart on mount
  React.useEffect(() => {
    console.log('Cart items:', cart);
  }, [cart]);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
          >
            <FiArrowLeft className="mr-1" /> Continue Shopping
          </button>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center">
            <FiShoppingCart className="mr-2" /> Your Shopping Cart
          </h1>
          <p className="text-gray-500 mt-2">
            {cartItemCount} item{cartItemCount !== 1 ? 's' : ''} in your cart
          </p>
        </div>
        
        {cart.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FiShoppingCart className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Your cart is empty</h2>
            <p className="text-gray-600 mb-6">Looks like you haven't added anything to your cart yet</p>
            <Link
              to="/"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
            >
              Browse Products
            </Link>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-2/3">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="divide-y divide-gray-200">
                  {cart.map(item => {
                    const quantity = item.quantity || 1;
                    const price = parseFloat(item.price || 0);
                    const maxQuantity = item.max_quantity || 10;
                    
                    return (
                      <div key={`${item.product_id}-${item.variation_id || 'no-var'}`} 
                           className="p-6 flex flex-col sm:flex-row">
                        <div className="flex-shrink-0 w-32 h-32 bg-gray-100 rounded-lg overflow-hidden">
                          <img 
                            src={item.image || 'https://via.placeholder.com/150'} 
                            alt={item.name} 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/150';
                            }}
                          />
                        </div>
                        
                        <div className="mt-4 sm:mt-0 sm:ml-6 flex-1">
                          <div className="flex justify-between">
                            <div>
                              <h3 className="text-lg font-medium text-gray-800">{item.name}</h3>
                              <p className="text-blue-600 font-medium">QAR {price.toFixed(2)}</p>
                              {/* Debug info - shows IDs */}
                              <div className="text-xs text-gray-400 mt-1">
                                ID: {item.product_id} | 
                                Var: {item.variation_id || 'none'} | 
                                Qty: {quantity}
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveItem(item)}
                              className="text-gray-400 hover:text-red-500 p-2 hover:bg-red-50 rounded transition-colors"
                              title="Remove from cart"
                            >
                              <FiTrash2 size={20} />
                            </button>
                          </div>
                          
                          {(item.color || item.size) && (
                            <div className="mt-2 space-y-1">
                              {item.color && (
                                <p className="text-sm text-gray-500">
                                  <span className="mr-1">Color:</span>
                                  <span className="text-gray-700">{item.color}</span>
                                </p>
                              )}
                              {item.size && (
                                <p className="text-sm text-gray-500">
                                  <span className="mr-1">Size:</span>
                                  <span className="text-gray-700">{item.size}</span>
                                </p>
                              )}
                            </div>
                          )}
                          
                          <div className="mt-4 flex items-center">
                            <button
                              onClick={() => handleDecrement(item)}
                              className={`p-2 rounded-l-lg border ${quantity <= 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                              disabled={quantity <= 1}
                            >
                              <FiMinus />
                            </button>
                            <span className="px-4 py-2 border-t border-b bg-gray-50 text-center min-w-[60px] font-medium">
                              {quantity}
                            </span>
                            <button
                              onClick={() => handleIncrement(item)}
                              className={`p-2 rounded-r-lg border ${quantity >= maxQuantity ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                              disabled={quantity >= maxQuantity}
                            >
                              <FiPlus />
                            </button>
                            
                            <div className="ml-auto text-lg font-medium">
                              QAR {(price * quantity).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="px-6 py-4 bg-gray-50 flex justify-between items-center">
                  <span className="text-gray-600">Total items: {cartItemCount}</span>
                  <button
                    onClick={clearCart}
                    className="text-red-600 hover:text-red-800 flex items-center px-4 py-2 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <FiTrash2 className="mr-2" /> Clear Cart
                  </button>
                </div>
              </div>
            </div>
            
            <div className="lg:w-1/3">
              <div className="bg-white rounded-xl shadow-sm p-6 sticky top-6">
                <h2 className="text-xl font-bold text-gray-800 mb-6">Order Summary</h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal ({cartItemCount} items)</span>
                    <span className="font-medium">QAR {cartTotal.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping</span>
                    <span className="text-green-600 font-medium">Calculated at checkout</span>
                  </div>
                  
                  <div className="flex justify-between pt-3 mt-2 border-t border-gray-200">
                    <span className="text-lg font-bold">Total</span>
                    <span className="text-lg font-bold">QAR {cartTotal.toFixed(2)}</span>
                  </div>
                </div>
                
                <button 
                  onClick={proceedToCheckout}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={cart.length === 0}
                >
                  {cart.length === 0 ? 'Cart is Empty' : 'Proceed to Checkout'}
                </button>
                
                <p className="mt-3 text-center text-sm text-gray-500">
                  <FiLock className="inline mr-1" />
                  Secure checkout process
                </p>
              </div>
              
              <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Need Help?</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Have questions about your order? Contact our customer support team.
                </p>
                <button className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors">
                  Contact Support
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Debug button - remove in production */}
      <button
        onClick={() => console.log('Current cart:', cart)}
        className="fixed bottom-4 right-4 bg-purple-600 text-white p-2 rounded-lg text-sm"
      >
        Debug Cart
      </button>
    </div>
  );
};

export default CartPage;